import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../../../button";
import { Image } from "../../../image";
import { useInboxRewardsContext } from "../../../../stores/inboxRewards";
import { ProgressSlideType } from ".";
import { useSessionContext } from "../../../../stores/session";
import { translate } from "./translations";

const STORAGE_PREFIX = "topsy-turvy-day-transition";
const CLAIMED_PREFIX = "topsy-turvy-claimed";

function readClaimedFromStorage(
    avatarId: number | null,
    slideId: string,
): Set<string> {
    try {
        const raw = localStorage.getItem(
            `${CLAIMED_PREFIX}-${avatarId}-${slideId}`,
        );
        if (!raw) return new Set();
        return new Set(JSON.parse(raw) as string[]);
    } catch {
        return new Set();
    }
}

function writeClaimedToStorage(
    avatarId: number | null,
    slideId: string,
    claimed: Set<string>,
) {
    try {
        localStorage.setItem(
            `${CLAIMED_PREFIX}-${avatarId}-${slideId}`,
            JSON.stringify([...claimed]),
        );
    } catch {
        // Private browsing or quota exceeded
    }
}

export function DayTransition({
    completedDay,
    onContinue,
    isFadingOut,
}: {
    completedDay: ProgressSlideType;
    onContinue: () => void;
    isFadingOut: boolean;
}) {
    const { actions } = useInboxRewardsContext();
    const {
        state: { avatarId },
    } = useSessionContext();
    const claimedVouchers = actions.getClaimedVouchers();

    const dayLabel =
        completedDay.relatedMedia?.find((m) => m.title === "day")?.label ?? "?";

    // Sync reward status from spellfactory on mount so rewards
    // already redeemed during the active day show as claimed
    const allVoucherIds = completedDay.milestones
        .filter((m) => m.unlocked && m.voucherId)
        .map((m) => m.voucherId as string);

    useEffect(() => {
        if (allVoucherIds.length > 0) {
            actions.fetchUnclaimedVouchers(allVoucherIds);
        }
    }, []);

    const milestoneLabels = useMemo(
        () =>
            new Map(
                completedDay.relatedMedia
                    ?.filter((media) => media.title?.startsWith("milestone-"))
                    .map((media) => {
                        const index =
                            media.title !== undefined
                                ? parseInt(
                                      media.title.replace("milestone-", ""),
                                      10,
                                  ) - 1
                                : -1;
                        return [index, media.label] as const;
                    })
                    .filter(([index]) => !isNaN(index)) ?? [],
            ),
        [completedDay.relatedMedia],
    );

    const claimableMilestones = completedDay.milestones
        .map((m, index) => ({ ...m, originalIndex: index }))
        .filter(
            (m) =>
                m.unlocked &&
                m.voucherId &&
                !claimedVouchers.includes(m.voucherId),
        );

    const [claimedInSession, setClaimedInSession] = useState<Set<string>>(() =>
        readClaimedFromStorage(avatarId, completedDay.id),
    );

    const allClaimed =
        claimableMilestones.length === 0 ||
        claimableMilestones.every((m) => claimedInSession.has(m.voucherId!));

    const handleClaimMilestone = (
        milestone: ProgressMilestone & { originalIndex: number },
    ) => {
        if (!milestone.voucherId || claimedInSession.has(milestone.voucherId)) {
            return;
        }

        try {
            actions.claimQuietVoucher(milestone.voucherId);
        } catch (err) {
            console.error("Failed to claim reward:", err);
        }

        setClaimedInSession((prev) => {
            const next = new Set([...prev, milestone.voucherId!]);
            writeClaimedToStorage(avatarId, completedDay.id, next);
            return next;
        });

        // Re-fetch status from spellfactory after a delay to confirm redemption
        setTimeout(() => {
            try {
                actions.fetchUnclaimedVouchers([milestone.voucherId!]);
            } catch {
                // Silent — UI already shows claimed
            }
        }, 1500);
    };

    const handleContinue = () => {
        try {
            localStorage.setItem(
                `${STORAGE_PREFIX}-${avatarId}-${completedDay.id}`,
                "true",
            );
        } catch {
            // Private browsing or quota exceeded — continue anyway
        }
        onContinue();
    };

    return (
        <div
            className={`topsy-turvy-day-transition ${isFadingOut ? "animating-out" : "animating-in"}`}
        >
            <div className="day-transition-header">
                <div className="day-transition-title">
                    {translate("dayComplete", completedDay.lang).replace(
                        "{day}",
                        dayLabel,
                    )}
                </div>
            </div>

            {claimableMilestones.length > 0 && (
                <div className="day-transition-rewards">
                    <div className="day-transition-rewards-label">
                        {translate("claimRewards", completedDay.lang)}
                    </div>
                    <div className="day-transition-milestones">
                        {claimableMilestones.map((milestone, index) => {
                            const isClaimed = claimedInSession.has(
                                milestone.voucherId!,
                            );
                            const multiplier = milestoneLabels.get(
                                milestone.originalIndex,
                            );
                            return (
                                <div
                                    key={index}
                                    className={`day-transition-milestone ${isClaimed ? "claimed" : "claimable"}`}
                                    onClick={() =>
                                        handleClaimMilestone(milestone)
                                    }
                                >
                                    {milestone.image && (
                                        <Image sources={milestone.image} />
                                    )}
                                    {multiplier && (
                                        <span className="milestone-multiplier">
                                            {multiplier}
                                        </span>
                                    )}
                                    {isClaimed && (
                                        <div className="claimed-check" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div
                className={`day-transition-continue ${claimableMilestones.length > 0 && !allClaimed ? "disabled" : ""}`}
            >
                <Button
                    onClick={handleContinue}
                    variant="green"
                    disabled={claimableMilestones.length > 0 && !allClaimed}
                >
                    <span className="buttonTextWrapper">
                        {translate("continue", completedDay.lang)}
                    </span>
                </Button>
            </div>
        </div>
    );
}

export function isDayTransitionSeen(
    slideId: string,
    avatarId: number | null,
): boolean {
    try {
        return (
            localStorage.getItem(`${STORAGE_PREFIX}-${avatarId}-${slideId}`) ===
            "true"
        );
    } catch {
        return false;
    }
}
